/**
 * OPS-PHASE1-009 — legacy module / permission aliases → canonical operational IDs.
 * Resolver and route guards normalize through these maps before matching registry entries.
 */

/** @type {Readonly<Record<string, string>>} legacy module id → canonical OPERATIONAL_MODULES id */
export const MODULE_ID_ALIASES = Object.freeze({
  RAD: 'RADIOLOGY_COUNTER',
  RAD_COUNTER: 'RADIOLOGY_COUNTER',
  RADIOLOGY: 'RADIOLOGY_COUNTER',
  RAD_VERIFICATION: 'RADIOLOGY_COUNTER',
  RADIOLOGY_VERIFICATION: 'RADIOLOGY_COUNTER',
  LAB: 'LAB_COUNTER',
  PHARMACY: 'PHARMACY_COUNTER',
  PHARMACY_DISPENSE: 'PHARMACY_COUNTER',
  PHARMACY_PROCURE: 'PHARMACY_PROCURE',
  PHARMACYPROCURE: 'PHARMACY_PROCURE',
  PHARMACY_GRN: 'PHARMACY_GRN',
  PHARMACYGRN: 'PHARMACY_GRN',
  PHARMACY_INVENTORY: 'PHARMACY_INVENTORY',
  PHARMACYINVENTORY: 'PHARMACY_INVENTORY',
  PHARMACY_ADJUST: 'PHARMACY_ADJUST',
  PHARMACYADJUST: 'PHARMACY_ADJUST',
  IP_INPATIENT: 'INPATIENT',
  URI_PATIENT_TOKEN: 'PATIENT_TOKEN',
  QUEUEBOARD: 'PATIENT_TOKEN',
  RECEPTION: 'PATIENT_REGISTRATION',
  PATIENTREGISTRATION: 'PATIENT_REGISTRATION',
  BILLING_WORKSPACE: 'OP_BILLING_COUNTER',
  OP_COUNTER: 'OP_BILLING_COUNTER',
  OPD_CONSULT: 'DOCTOR_CONSULTATION',
  OPD_CONSULTATION: 'DOCTOR_CONSULTATION',
  CLINICAL_CONSULTATION: 'DOCTOR_CONSULTATION',
});

/** @type {Readonly<Record<string, string>>} legacy workflow group → canonical group code */
export const WORKFLOW_GROUP_ALIASES = Object.freeze({
  RAD_COUNTER: 'RADIOLOGY_COUNTER',
  RAD_VERIFICATION: 'RADIOLOGY_VERIFICATION',
});

/**
 * Legacy permission / URI id tokens → canonical permission suffix (tenant prefix applied at runtime).
 * @type {Readonly<Record<string, string>>}
 */
export const PERMISSION_KEY_ALIASES = Object.freeze({
  HMS_OPERATIONS_ALL: 'HMSOPERATIONSALL',
  OPERATIONAL_ALL: 'HMSOPERATIONSALL',
  SYSTEM_ADMIN_OPERATIONS: 'HMSOPERATIONSALL',
  BILLING_WORKSPACE: 'BILLINGWORKSPACE',
  BILLING_SERVICE_CATALOG: 'BILLINGSERVICECATALOG',
  BILLINGSERVICECATALOG: 'BILLINGSERVICECATALOG',
  BILLING_OP_COUNTER: 'OPBILLINGCOUNTER',
  OP_BILLING_COUNTER: 'OPBILLINGCOUNTER',
  PATIENT_JOURNEY: 'PATIENTJOURNEY',
  BILLING_PATIENT_JOURNEY: 'PATIENTJOURNEY',
  OPERATIONAL_PATIENT_JOURNEY: 'PATIENTJOURNEY',
  PATIENT_TOKEN: 'PATIENTTOKEN',
  URI_PATIENT_TOKEN: 'PATIENTTOKEN',
  RECEPTION_TOKEN: 'PATIENTTOKEN',
  PATIENTS: 'PATIENTS',
  PATIENT_REGISTRATION: 'PATIENTS',
  SHIFT_CONTINUITY: 'SHIFTCONTINUITY',
  COUNTER_SHIFT: 'SHIFTCONTINUITY',
  BILLING_SHIFT_CONTINUITY: 'SHIFTCONTINUITY',
  IP_DISCHARGE: 'IPDISCHARGE',
  DISCHARGE_DESK: 'IPDISCHARGE',
  BILLING_IP_DISCHARGE: 'IPDISCHARGE',
  BILLING_RECEIVABLES: 'BILLINGRECEIVABLES',
  // Canonical inpatient capability (V102). Do not alias to IPDISCHARGE.
  INPATIENT: 'INPATIENT',
  IP_INPATIENT: 'INPATIENT',
  REFUND_REVIEW: 'REFUNDREVIEW',
  BILLING_REFUND_REVIEW: 'REFUNDREVIEW',
  OPERATIONAL_RECOVERY: 'OPERATIONALRECOVERY',
  OPERATIONAL_READINESS: 'OPERATIONALREADINESS',
  OPERATIONAL_ALERTS: 'OPERATIONALALERTS',
  BILLING_OPERATIONAL_ALERTS: 'OPERATIONALALERTS',
  OPS_ALERTS: 'OPERATIONALALERTS',
  FINANCE_AUDIT: 'COUNTERFINANCEAUDIT',
  COUNTER_FINANCE_AUDIT: 'COUNTERFINANCEAUDIT',
  FINANCE_CLOSE: 'FINANCECLOSE',
  BILLING_FINANCE_AUDIT: 'FINANCECLOSE',
  BILLING_DISCOUNT_APPROVAL: 'OPERATIONALAPPROVALS',
  OPERATIONAL_APPROVALS: 'OPERATIONALAPPROVALS',
  FONE_URI_OP_APPROVALS: 'OPERATIONALAPPROVALS',
  LAB_COUNTER: 'LABCOUNTER',
  LAB_VERIFICATION: 'LABVERIFICATION',
  LAB_QUEUE: 'LABCOUNTER',
  RAD_COUNTER: 'RADIOLOGYCOUNTER',
  RADIOLOGY_COUNTER: 'RADIOLOGYCOUNTER',
  RAD_VERIFICATION: 'RADIOLOGYVERIFICATION',
  RADIOLOGY_VERIFICATION: 'RADIOLOGYVERIFICATION',
  PHARMACY_COUNTER: 'PHARMACYCOUNTER',
  PHARMACY_DISPENSE: 'PHARMACYISSUE',
  PHARMACY_ISSUE: 'PHARMACYISSUE',
  PHARMACY_RETURNS: 'PHARMACYRETURNS',
  PHARMACY_PRINT_QUEUE: 'PHARMACYPRINTQUEUE',
  PHARMACY_PROCURE: 'PHARMACYPROCURE',
  PHARMACYPROCURE: 'PHARMACYPROCURE',
  PHARMACY_GRN: 'PHARMACYGRN',
  PHARMACYGRN: 'PHARMACYGRN',
  PHARMACY_INVENTORY: 'PHARMACYINVENTORY',
  PHARMACYINVENTORY: 'PHARMACYINVENTORY',
  PHARMACY_ADJUST: 'PHARMACYADJUST',
  PHARMACYADJUST: 'PHARMACYADJUST',
  OPD_CONSULTATION: 'OPDCONSULTATION',
  DOCTOR_CONSULTATION: 'OPDCONSULTATION',
  CLINICAL_CONSULTATION: 'OPDCONSULTATION',
  CLINICAL_RECOMMENDATION_ENTRY: 'CLINICALRECOMMENDATIONS',
});

export function normKey(s) {
  return String(s ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

/**
 * @param {string} raw
 * @param {Set<string>} [knownModuleIds]
 */
export function normalizeModuleId(raw, knownModuleIds = null) {
  const key = normKey(raw);
  if (!key) return '';
  if (knownModuleIds?.has(key)) return key;
  const aliased = MODULE_ID_ALIASES[key];
  if (aliased && (!knownModuleIds || knownModuleIds.has(aliased))) return aliased;
  return aliased ?? key;
}

/** Expand legacy permission token to canonical suffix (tenant prefix applied at runtime). */
export function canonicalPermissionKey(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const colon = trimmed.indexOf(':');
  if (colon > 0) {
    const prefix = trimmed.slice(0, colon).trim();
    // Only strip real tenant/apps prefixes. Never treat DOCUMENTAI:API as tenant:API.
    if (/^(FONE|CHERITTO|REG[0-9A-Z]+)$/i.test(prefix)) {
      return trimmed.slice(colon + 1).trim();
    }
    return trimmed;
  }
  const mapped = PERMISSION_KEY_ALIASES[normKey(trimmed)];
  return mapped ?? trimmed;
}

export function normalizeWorkflowGroupCode(raw) {
  const key = normKey(raw);
  return WORKFLOW_GROUP_ALIASES[key] ?? key;
}
